import Model from "oc-jaco3d/arrangement/Model";
import Booth3d from "../arrangement3d/Booth3d";
import { v3 } from "oc-modules/oc-threejs/short3";
import PlaneInBoxRestraint from "oc-modules/oc-interaction/restraints2/PlaneInBoxRestraint";
import PlaneRestraint from "oc-modules/oc-interaction/restraints2/PlaneRestraint";
import SurfaceRestraint from "oc-modules/oc-interaction/restraints2/SurfaceRestraint";
import Scenes from "oc-modules/oc-threejs/Scenes";
import Interior from "./Interior";

export default class Booth extends Model {
  interiors = [];
  extras = [];

  // Erleichtert den Zugriff etwas.
  // Daten aus dem Arrangement die für die Ermittlung des eigenen States notwendig sind
  updateState = {
    selectedColors: [],
  };

  state = {
    colorPrefixes: [],
    boothColors: [],
  };

  collisionTolerance = 0.01;

  constructor(parent, key) {
    super(parent, key);
  }

  addInterior(interior) {
    this.interiors.push(interior);
  }

  removeInterior(interior) {
    this.interiors.remove((i) => i == interior);
  }

  clearInteriors() {
    this.interiors = [];
  }

  checkInteriorCollisions() {
    const l = this.interiors.length;
    const restraint = APP.arrangement?.interiorRestraint;
    const object3d = APP.arrangement?.currentInterior?.jaco3d;
    if (restraint && object3d) {
      restraint.dragObject3d = object3d;
      restraint.updateCollisionObjects();
      const collision = restraint.checkCollision();
      object3d.jaco.positionValid = !collision;
      object3d.onChangeInDragSelectHoverState();
    }
  }

  updateExtras(item, amount) {
    // Sanetizing input
    amount = parseInt(amount);
    if (Number.isNaN(amount) || amount < 0) amount = 0;
    if (amount > 99) amount = 99;

    if (item.DefaultArticle) item = item.DefaultArticle;
    // Check if "Extra" was already added to booth --> only change amount
    const existingIndex = this.extras.findIndex((int) => int.id == item.id);
    if (existingIndex >= 0) {
      if (amount == 0) this.extras.splice(existingIndex, 1);
      else this.extras[existingIndex].amount = amount;
    } else {
      if (amount != 0) {
        item.amount = amount;
        this.extras.push(item);
      }
    }
    APP.arrangement.partslist.update();
    APP.ui.dirty();
  }

  update3d(updateState) {
    super.update3d();
    this.updateState = updateState;
    this.updateBoothColors();
    this.interiors.forEach((interior) => interior.update3d());
  }

  updateBoothColors() {
    this.state.boothColors = this.updateState.selectedColors;
  }

  compileFloorRestraints() {
    const restraint = new (class extends PlaneRestraint {
      perform(raycaster, point, dropped) {
        if (this.freeOrientation) {
          Scenes.bring({
            what: [this.dragObject3d, this.anchorMatrix, v3(0, 1, 0)],
            to: [this.matrixWorld, this.plane.normal],
            rotate: true,
          });
        } else {
          Scenes.bring({
            what: [this.dragObject3d, this.anchorMatrix],
            to: [this.matrixWorld],
            rotate: true,
          });
        }

        if (this.box) {
          const oldValid = Object.assign({}, { value: this.dragObject3d.jaco.positionValid });
          const objectBox = this.getObjectBox(this.dragObject3d);
          const box = this.box.clone();
          box.expandByVector(objectBox.getSize().multiplyScalar(-0.5));
          this.dragObject3d.jaco.positionValid =
            point.x >= box.min.x &&
            point.x <= box.max.x &&
            point.z >= box.min.z &&
            point.z <= box.max.z;

          if (oldValid.value !== this.dragObject3d.jaco.positionValid) {
            this.dragObject3d.onChangeInDragSelectHoverState();
          }
        }

        Scenes.bring({
          what: [this.dragObject3d, this.anchorMatrix],
          to: [point],
          translate: true,
        });
      }
    })({
      priority: 2,
      box: this.jaco3d.posbox.box,
      defaultAnchorBoxPosition: v3(0, 0.0, 0),
      freeOrientation: true,
    });

    APP.arrangement.boothFloors[this.key] = restraint;

    // filter über metadata; aktuell nicht im Angebot!
    // const metaData = this.getMeta();
    // const floorFilter = ["Rectangle1111150181"]; //metaData.floorFilter.split(',');
    // const floor = Scenes.filter(this.jaco3d, o=> floorFilter.includes(o.name))[0];
    // APP.arrangement.boothFloors[this.key] = new SurfaceRestraint({
    //     priority: 0,
    //     parentObject3D: floor,
    //     freeOrientationAroundMainAxis: true
    // });
  }

  createJaco3d() {
    return new Booth3d(this);
  }

  toJSON() {
    const json = super.toJSON();
    json.selectedSize = this.selectedSize;
    json.interiors = this.interiors.map((i) => i.toJSON());
    json.extras = this.extras.map((e) => {
      const extra = {};
      extra.articleNumber = e.Artikelnummer;
      extra.amount = e.amount;
      return extra;
    });
    return json;
  }

  fromJSON(json) {
    super.fromJSON(json);
    this.selectedSize = json.selectedSize;
    this.interiors = json.interiors.map((i) => {
      return new Interior(this, null, this).fromJSON(i);
    });
    this.extras = json.extras.map((e) => {
      const extra = APP.richMetadata.metadata.Artikel.find((a) => a.Artikelnummer == e.articleNumber);
      extra.amount = e.amount || 0;
      return extra;
    });
    if (json.Inspiration) {
      this.basicInteriors = json.interiors.filter(i => i.basicEquipment).map((i) => {
        return new Interior(this, null, this).fromJSON(i);
      });
      this.basicInteriorArticles = this.basicInteriors.map((interior) => {
        return APP.richMetadata.metadata.Artikel.find((art) => art.Artikelnummer === interior.article.Artikelnummer);
      });

      this.basicExtras = this.extras.filter(e => e.Artikelnummer.match(/BasicEquip/)).map(extra => {
        const basicExtra = APP.configuration.Json?.partslist?.parts?.find(p => p.title == extra.Artikeltext);
        const basicAmount = basicExtra?.amount || 0;
        return {
          ...extra,
          amount: basicAmount || extra.amount || 0
        }
      });
      this.basicExtraArticles = this.basicExtras.map((extra) => {
        // const basicExtra = APP.configuration.Json.partslist.parts.find(p => p.type == "BasicEquipment" && p.title == extra.Artikeltext);
        // const basicAmount = basicExtra?.amount || 0;
        return {
          ...APP.richMetadata.metadata.Artikel.find((art) => art.Artikelnummer === extra.Artikelnummer),
          amount: extra.amount,
        };
      });
    }
    return this;
  }
}
